import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { LegNode, BrainField, Lead } from '../../types';
import * as api from '../../services/api';
import { SYSTEM_FIELDS, isSystemFieldId, systemFieldLabel } from './systemFields';
import ClientAssignmentField from '../shared/ClientAssignmentField';

// (Used by Condition leg's branch picker — populated from the data body's other legs.)
type LegOption = { id: string; name: string; type: string };

interface Props {
  legNode: LegNode;
  onSave: (config: Record<string, any>) => void;
  onClose: () => void;
  /** Called after the Client field writes an assignment, so the leg column
   *  can relabel the node card without a full page reload. */
  onClientAssigned?: () => void;
}

// Viewport-aware tooltip. Renders the bubble via a portal at document.body
// so it escapes any ancestor with overflow:hidden (the modal shell) and
// clamps horizontally so it never overflows the viewport on either edge.
// The `align` prop is kept for backward compat but is no longer needed —
// positioning is automatic. Defaults to "above the icon" and flips below
// if there isn't room (near the top of the viewport).
function FieldTooltip({ text }: { text: string; align?: 'center' | 'right' }) {
  const [coords, setCoords] = useState<{ left: number; top: number; placeBelow: boolean } | null>(null);
  const iconRef = React.useRef<HTMLSpanElement>(null);

  const TOOLTIP_WIDTH = 280;
  const EDGE_MARGIN = 8;
  const TOOLTIP_EST_HEIGHT = 80;

  const handleEnter = () => {
    if (!iconRef.current) {return;}
    const rect = iconRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    left = Math.max(EDGE_MARGIN, Math.min(left, vw - TOOLTIP_WIDTH - EDGE_MARGIN));
    const placeBelow = rect.top < TOOLTIP_EST_HEIGHT + EDGE_MARGIN;
    const top = placeBelow ? rect.bottom + 6 : rect.top - 6;
    setCoords({ left, top, placeBelow });
  };

  return (
    <span
      ref={iconRef}
      style={{ position: 'relative', display: 'inline-block', marginLeft: 6, color: '#9ca3af', cursor: 'help', fontWeight: 'normal' }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setCoords(null)}
    >
      &#9432;
      {coords && createPortal(
        <span
          style={{
            position: 'fixed',
            left: coords.left,
            top: coords.top,
            transform: coords.placeBelow ? 'none' : 'translateY(-100%)',
            width: TOOLTIP_WIDTH,
            padding: '8px 10px',
            background: '#1f2937',
            color: '#f3f4f6',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.45,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            fontWeight: 'normal',
            whiteSpace: 'normal',
            textAlign: 'left',
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}

// Internal editable shapes — give every row a stable id so React can
// identify them across renders. Without this, typing into one of the inputs
// would lose focus on every keystroke (the row's React key would change as
// the destination-field text changed).
type EditableMapping = { id: string; outField: string; leadField: string; required: boolean };
type EditableStatic = { id: string; outField: string; value: string };

function newId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// One row of the "Test A Lead" form. `leadField` is the source key we put the
// value under in testData (what the backend maps from). `destLabels` are the
// destination field(s) this value feeds, shown so the user sees what's going
// out. `system` rows are read-only — the server computes them at send time.
type TestField = { leadField: string; destLabels: string[]; value: string; system: boolean };

// Guess a realistic sample value from a lead field's name, so the tester is
// pre-filled (john-smith style) and the user can just hit Submit. Falls back to
// a generic token. Matching is loose/substring and case-insensitive.
function sampleLeadValue(name: string): string {
  const n = (name || '').toLowerCase();
  const has = (...keys: string[]) => keys.some(k => n.includes(k));
  if (has('email', 'e-mail')) {return 'testlead@example.com';}
  if (has('first', 'fname', 'f_name')) {return 'John';}
  if (has('last', 'lname', 'l_name', 'surname')) {return 'Smith';}
  if (has('fullname', 'full_name') || n === 'name') {return 'John Smith';}
  if (has('company', 'business', 'org')) {return 'Acme Co';}
  if (has('mobile', 'cell')) {return '5551234567';}
  if (has('phone', 'tel')) {return '5551234567';}
  if (has('zip', 'postal')) {return '10010';}
  if (has('city')) {return 'New York';}
  if (has('state', 'region', 'province')) {return 'NY';}
  if (has('country')) {return 'US';}
  if (has('address', 'street', 'addr', 'address1')) {return '123 Main St';}
  if (has('ip')) {return '203.0.113.10';}
  if (has('gender', 'sex')) {return 'M';}
  if (has('dob', 'birth')) {return '07/12/1988';}
  if (has('age')) {return '37';}
  if (has('url', 'website', 'link')) {return 'https://example.com';}
  return 'test';
}

// For the Failed-leads view: walk the lead's processing_log newest-to-oldest
// and return the most recent fail/error message recorded for THIS leg node.
// Returns '' if none found (shouldn't happen — the lead is in the failed list
// because such an entry exists — but guard anyway).
function findFailReason(lead: Lead, legNodeId: string): string {
  const log = lead.processingLog;
  if (!Array.isArray(log)) {return '';}
  for (let i = log.length - 1; i >= 0; i--) {
    const e = log[i];
    if (e.legNodeId === legNodeId && (e.status === 'fail' || e.status === 'error')) {
      return e.message || `(no message; status=${e.status})`;
    }
  }
  return '';
}

function LegNodeConfigModal({ legNode, onSave, onClose, onClientAssigned }: Props) {
  const [config, setConfig] = useState<Record<string, any>>(legNode.config || {});
  // Raw text backing the Custom Headers (JSON) input. Kept separate from the
  // parsed object so mid-typing input (which is transiently invalid JSON) is
  // never discarded — otherwise the field is un-typeable (every keystroke
  // fails JSON.parse and the controlled value snaps back to empty).
  const [headersRaw, setHeadersRaw] = useState<string>(
    legNode.config?.webhookHeaders ? JSON.stringify(legNode.config.webhookHeaders) : ''
  );
  const [headersError, setHeadersError] = useState<string | null>(null);

  // webhook_output and custom_api both use the same URL/method/headers/mapping config
  // (LegRouter.processCustomApi delegates to processWebhook).
  const isUrlType = legNode.type === 'webhook_output' || legNode.type === 'custom_api';

  // Brain fields for the lead-field dropdown.
  const [brainFields, setBrainFields] = useState<BrainField[]>([]);

  // Pass/Fail stats + drill-down state (parallels VertebraConfigModal).
  // `today` block added 2026-05-18 — pass/fail at this leg-node for
  // leads received since local midnight in the org's timezone.
  // Skeleton shimmer during load — see [[feedback_deferred_load_pattern]].
  const [stats, setStats] = useState<{ pass: number; fail: number; today?: { pass: number; fail: number } } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [view, setView] = useState<'config' | 'leads'>('config');
  const [leadsFilter, setLeadsFilter] = useState<'pass' | 'fail'>('pass');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  // Outbound API Log popup — the lead whose request/response is being shown.
  const [apiLogLead, setApiLogLead] = useState<Lead | null>(null);
  const [apiLog, setApiLog] = useState<api.DispatchLog | null>(null);
  const [apiLogLoading, setApiLogLoading] = useState(false);
  const [apiLogFullResponse, setApiLogFullResponse] = useState(false);

  // For Condition legs: list of OTHER legs in this data body, used to populate
  // the true/false branch dropdowns.
  const [siblingLegs, setSiblingLegs] = useState<LegOption[]>([]);

  // "Test A Lead" popup — send one synthetic lead through the real outbound
  // path to confirm the destination accepts the data before the feed is on.
  const [testOpen, setTestOpen] = useState(false);
  const [testFields, setTestFields] = useState<TestField[]>([]);
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState<api.TestLeadResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testFullResponse, setTestFullResponse] = useState(false);

  // Editable arrays (with stable IDs) for field mapping + static values.
  // Initialized from config on mount, converted back to Record<string, string> on save.
  const [mappings, setMappings] = useState<EditableMapping[]>(() => {
    // `requiredFields` is a parallel list of destination-field names that must
    // be present on the lead (see LegNodeConfig). Hydrate each row's toggle
    // from it. Absent on older configs → every row defaults to optional.
    const requiredSet = new Set<string>(
      Array.isArray(legNode.config?.requiredFields) ? legNode.config!.requiredFields : []
    );
    return Object.entries(legNode.config?.fieldMapping || {}).map(([outField, leadField]) => ({
      id: newId(),
      outField,
      leadField: String(leadField),
      required: requiredSet.has(outField),
    }));
  });
  const [statics, setStatics] = useState<EditableStatic[]>(() =>
    Object.entries(legNode.config?.staticValues || {}).map(([outField, value]) => ({
      id: newId(),
      outField,
      value: String(value),
    }))
  );

  useEffect(() => {
    // Always fetch brain fields — used by URL-type legs (field-mapping
    // dropdown) AND by Condition legs (the "If field" picker).
    if (isUrlType || legNode.type === 'condition') {
      api.getBrainFields(legNode.dataBodyId)
        .then(setBrainFields)
        .catch(err => console.error('Failed to load brain fields:', err));
    }
    // Lifetime pass/fail stats for this leg node.
    setStatsLoading(true);
    api.getLegNodeStats(legNode.dataBodyId, legNode.id)
      .then(s => {
        setStats(s);
        setStatsLoading(false);
      })
      .catch(err => {
        console.error('Failed to load leg node stats:', err);
        setStats({ pass: 0, fail: 0 });
        setStatsLoading(false);
      });
    // For Condition legs, load siblings for the branch dropdowns. Scoped to
    // the SAME leg as this condition — cross-leg branching breaks the
    // "each leg is an independent decision tree" model. legId on each node
    // is set since migration 005.
    if (legNode.type === 'condition') {
      api.getLegNodes(legNode.dataBodyId)
        .then(allNodes => setSiblingLegs(
          allNodes
            .filter(l => l.legId === legNode.legId) // same leg only
            .filter(l => l.id !== legNode.id)        // exclude self to prevent loops
            .map(l => ({ id: l.id, name: l.name, type: l.type }))
        ))
        .catch(err => console.error('Failed to load sibling leg nodes:', err));
    }
  }, [legNode.id, legNode.dataBodyId, legNode.type, legNode.legId, isUrlType]);

  async function openLeadsView(filter: 'pass' | 'fail') {
    setLeadsFilter(filter);
    setView('leads');
    setLoadingLeads(true);
    try {
      const recent = await api.getLegNodeLeads(legNode.dataBodyId, legNode.id, filter, 20);
      setLeads(recent);
    } catch (err) {
      console.error('Failed to load leg leads:', err);
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  }

  // Open the outbound API Log popup for one lead — the exact request we sent
  // to the destination and the response we got back.
  async function openApiLog(lead: Lead) {
    setApiLogLead(lead);
    setApiLog(null);
    setApiLogFullResponse(false);
    setApiLogLoading(true);
    try {
      const log = await api.getLegNodeDispatchLog(legNode.dataBodyId, legNode.id, lead.id);
      setApiLog(log);
    } catch (err) {
      console.error('Failed to load dispatch log:', err);
      setApiLog(null);
    } finally {
      setApiLogLoading(false);
    }
  }

  function formatReceived(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function updateConfig(key: string, value: any) {
    setConfig(prev => ({ ...prev, [key]: value }));
  }

  // Fold the editable mapping/static rows back into a config object. Shared by
  // Save and by "Test A Lead" (so a test reflects the current, possibly
  // unsaved, edits). Drops rows with empty destination fields.
  function buildConfig(): Record<string, any> {
    const fieldMapping: Record<string, string> = {};
    const requiredFields: string[] = [];
    mappings.forEach(({ outField, leadField, required }) => {
      const k = outField.trim();
      if (k && leadField) {
        fieldMapping[k] = leadField;
        if (required) {requiredFields.push(k);}
      }
    });
    const staticValues: Record<string, string> = {};
    statics.forEach(({ outField, value }) => {
      const k = outField.trim();
      if (k) {staticValues[k] = value;}
    });
    const finalConfig = { ...config };
    if (Object.keys(fieldMapping).length > 0) {finalConfig.fieldMapping = fieldMapping;}
    else {delete finalConfig.fieldMapping;}
    if (requiredFields.length > 0) {finalConfig.requiredFields = requiredFields;}
    else {delete finalConfig.requiredFields;}
    if (Object.keys(staticValues).length > 0) {finalConfig.staticValues = staticValues;}
    else {delete finalConfig.staticValues;}
    return finalConfig;
  }

  function handleSave() {
    onSave(buildConfig());
  }

  // Common lead fields to pre-fill when the leg has NO field mapping (the whole
  // lead is sent as-is, so testData keys become the payload keys directly).
  const DEFAULT_TEST_FIELDS = ['email', 'fname', 'lname', 'phone', 'address', 'city', 'state', 'zip'];

  // Open the tester: derive the rows to pre-fill from the current mapping.
  function openTest() {
    setTestResult(null);
    setTestError(null);
    setTestFullResponse(false);

    const rows: TestField[] = [];
    const byLeadField = new Map<string, TestField>();

    // Only rows with both a destination name and a chosen lead field ship.
    const active = mappings.filter(m => m.outField.trim() && m.leadField);
    if (active.length > 0) {
      for (const m of active) {
        const existing = byLeadField.get(m.leadField);
        if (existing) {
          existing.destLabels.push(m.outField.trim());
          continue;
        }
        const system = isSystemFieldId(m.leadField);
        const row: TestField = {
          leadField: m.leadField,
          destLabels: [m.outField.trim()],
          value: system ? `(auto: ${systemFieldLabel(m.leadField)})` : sampleLeadValue(m.leadField),
          system,
        };
        byLeadField.set(m.leadField, row);
        rows.push(row);
      }
    } else {
      // No mapping → whole lead sent as-is. Seed a sensible default set.
      for (const f of DEFAULT_TEST_FIELDS) {
        rows.push({ leadField: f, destLabels: [f], value: sampleLeadValue(f), system: false });
      }
    }

    setTestFields(rows);
    setTestOpen(true);
  }

  function updateTestField(leadField: string, value: string) {
    setTestFields(prev => prev.map(r => r.leadField === leadField ? { ...r, value } : r));
  }

  async function runTest() {
    setTestRunning(true);
    setTestResult(null);
    setTestError(null);
    setTestFullResponse(false);
    try {
      // Build the synthetic lead. Skip system rows — the server computes those.
      const testData: Record<string, any> = {};
      for (const r of testFields) {
        if (r.system) {continue;}
        testData[r.leadField] = r.value;
      }
      const result = await api.testLegNode(legNode.dataBodyId, legNode.id, {
        config: buildConfig(),
        testData,
      });
      setTestResult(result);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.message || 'Test failed';
      setTestError(String(detail));
    } finally {
      setTestRunning(false);
    }
  }

  // Field-mapping helpers
  function addMapping() {
    setMappings(prev => [...prev, { id: newId(), outField: '', leadField: '', required: false }]);
  }
  function updateMapping(id: string, patch: Partial<EditableMapping>) {
    setMappings(prev => prev.map(m => m.id === id ? { ...m, ...patch } : m));
  }
  function removeMapping(id: string) {
    setMappings(prev => prev.filter(m => m.id !== id));
  }

  // Static-values helpers
  function addStatic() {
    setStatics(prev => [...prev, { id: newId(), outField: '', value: '' }]);
  }
  function updateStatic(id: string, patch: Partial<EditableStatic>) {
    setStatics(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));
  }
  function removeStatic(id: string) {
    setStatics(prev => prev.filter(s => s.id !== id));
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: view === 'leads' ? 980 : 600,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header with title, type, and pass/fail stats top-right.
            Stats render with skeleton shimmer while loading so the
            modal frame paints instantly. See
            [[feedback_deferred_load_pattern]]. */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <h2 className="modal-title" style={{ margin: 0 }}>Configure: {legNode.name}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', textAlign: 'right', whiteSpace: 'nowrap', marginTop: 4 }}>
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6b7280' }}>
              {statsLoading ? (
                <>
                  <span style={{ color: '#059669', fontSize: 11, fontWeight: 500 }}>
                    Pass: <span className="stat-skeleton" style={{ width: 40, height: 11, verticalAlign: 'middle', display: 'inline-block' }} aria-label="loading pass count" />
                  </span>
                  <span style={{ color: '#dc2626', fontSize: 11, fontWeight: 500 }}>
                    Failed: <span className="stat-skeleton" style={{ width: 40, height: 11, verticalAlign: 'middle', display: 'inline-block' }} aria-label="loading fail count" />
                  </span>
                </>
              ) : stats ? (
                <>
                  <button
                    onClick={() => openLeadsView('pass')}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#059669', fontSize: 11, fontWeight: 500, textDecoration: 'underline' }}
                    title="View 20 most recent leads that passed this leg"
                  >
                    Pass: {stats.pass.toLocaleString()}
                  </button>
                  <button
                    onClick={() => openLeadsView('fail')}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#dc2626', fontSize: 11, fontWeight: 500, textDecoration: 'underline' }}
                    title="View 20 most recent leads that failed at this leg"
                  >
                    Failed: {stats.fail.toLocaleString()}
                  </button>
                </>
              ) : null}
            </div>
            {statsLoading ? (
              <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6366f1' }}>
                <span>Today pass: <span className="stat-skeleton" style={{ width: 30, height: 11, verticalAlign: 'middle', display: 'inline-block' }} aria-label="loading today pass" /></span>
                <span>Today failed: <span className="stat-skeleton" style={{ width: 30, height: 11, verticalAlign: 'middle', display: 'inline-block' }} aria-label="loading today fail" /></span>
              </div>
            ) : stats?.today ? (
              <div
                style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6366f1' }}
                title="Pass/fail at this leg node for leads received today (local midnight in the organization's timezone)."
              >
                <span>Today pass: {stats.today.pass.toLocaleString()}</span>
                <span>Today failed: {stats.today.fail.toLocaleString()}</span>
              </div>
            ) : null}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>
          Type: {legNode.type.replace(/_/g, ' ')}
        </div>

        {/* Client — deliberately the first thing in the modal. Who a
            destination delivers to is the only decision in here that
            cannot be undone once a lead has gone out. Self-contained:
            it fetches and writes the assignment itself rather than
            riding on this modal's own save flow. */}
        <ClientAssignmentField
          dataBodyId={legNode.dataBodyId}
          legNodeId={legNode.id}
          nodeType={legNode.type}
          onAssigned={onClientAssigned}
        />

        {/* Leads view — drill-down table */}
        {view === 'leads' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <button onClick={() => setView('config')} className="btn btn-secondary btn-sm">
                &larr; Back to config
              </button>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Last 20 leads &middot; {leadsFilter === 'pass' ? 'passed this leg' : 'failed at this leg'} &middot; showing {leads.length}
              </div>
            </div>
            <div style={{ overflow: 'auto', maxHeight: '60vh', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              {loadingLeads ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading...</div>
              ) : leads.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                  No leads have {leadsFilter === 'pass' ? 'passed' : 'failed at'} this leg yet.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: '#f8f9fb', zIndex: 1, boxShadow: 'inset 0 -1px 0 #e5e7eb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Received</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>API Log</th>
                      {leadsFilter === 'fail' && (
                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Reason</th>
                      )}
                      {brainFields.map(f => (
                        <th key={f.id} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{f.fieldLabel}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, i) => {
                      const reason = leadsFilter === 'fail' ? findFailReason(lead, legNode.id) : '';
                      return (
                        <tr key={lead.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>{formatReceived(lead.receivedAt)}</td>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                            {lead.dispatchLog ? (
                              <button
                                onClick={() => openApiLog(lead)}
                                title="View the exact request we sent and the response the destination returned"
                                style={{
                                  cursor: 'pointer',
                                  border: '1px solid #d1d5db',
                                  background: '#fff',
                                  borderRadius: 6,
                                  padding: '2px 8px',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: lead.dispatchLog.category === 'delivered' ? '#047857' : '#b91c1c',
                                }}
                              >
                                API{lead.dispatchLog.statusCode != null ? ` · ${lead.dispatchLog.statusCode}` : ''}
                              </button>
                            ) : (
                              <span style={{ color: '#d1d5db' }}>&mdash;</span>
                            )}
                          </td>
                          {leadsFilter === 'fail' && (
                            <td
                              title={reason}
                              style={{ padding: '10px 12px', color: '#b91c1c', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {reason || <span style={{ color: '#d1d5db' }}>&mdash;</span>}
                            </td>
                          )}
                          {brainFields.map(f => {
                            // Enriched attributes (enrich_*, bigsky_*) live on the
                            // lead's enrichedData, not its original data.
                            const v = lead.enrichedData?.[f.fieldName] ?? lead.data?.[f.fieldName];
                            const display = Array.isArray(v) ? v.join(', ') : v;
                            return (
                              <td key={f.id} style={{ padding: '10px 12px', color: '#111827', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {display != null && display !== '' ? String(display) : <span style={{ color: '#d1d5db' }}>&mdash;</span>}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </>
        )}

        {/* Config view */}
        {view === 'config' && (
          <>

        {/* Webhook Output / Custom API — URL, method, headers, mapping */}
        {isUrlType && (
          <>
            <div className="form-group">
              <label className="form-label">
                URL
                <FieldTooltip text="The destination URL the lead will be sent to. Must include the protocol (https://...). Example: https://api.example.com/leads or https://hooks.zapier.com/...." />
              </label>
              <input
                className="form-input"
                placeholder="https://example.com/leads"
                value={config.webhookUrl || ''}
                onChange={e => updateConfig('webhookUrl', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">
                  Method
                  <FieldTooltip text="HTTP method. POST for most modern webhooks. GET if the destination reads via URL query string (older endpoints, tracking pixels). PUT for upserts." />
                </label>
                <select
                  className="form-select"
                  value={config.webhookMethod || 'POST'}
                  onChange={e => updateConfig('webhookMethod', e.target.value)}
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="GET">GET</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">
                  Body Format
                  <FieldTooltip text="How the data is encoded in the request. JSON: modern APIs (Content-Type: application/json). Form-encoded: older PHP endpoints, classic web forms (Content-Type: application/x-www-form-urlencoded — what PHP's $_POST expects). Multipart form-data: sends each value verbatim (Content-Type: multipart/form-data), so characters like a phone's leading + are NOT altered — also lands in PHP's $_POST. Ignored when method is GET (data always goes in the URL query string)." />
                </label>
                <select
                  className="form-select"
                  value={config.bodyFormat || 'json'}
                  onChange={e => updateConfig('bodyFormat', e.target.value)}
                  disabled={config.webhookMethod === 'GET'}
                >
                  <option value="json">JSON</option>
                  <option value="form">Form-encoded</option>
                  <option value="multipart">Multipart form-data</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Send rate
                <FieldTooltip text="How fast this leg pushes leads to the destination URL across ALL leads. Two examples to understand the shape: ‘1 lead every 30 seconds’ paces them out one-at-a-time (good for sensitive APIs); ‘10 leads every 60 seconds’ allows up to 10 in any rolling 60-second window (the bucket can burst — if 10 arrive at once they all go, then nothing for the rest of the window). Tune the destination, not the data." />
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                <span style={{ color: '#6b7280' }}>Send up to</span>
                <input
                  type="number"
                  className="form-input"
                  placeholder="5"
                  min={1}
                  style={{ width: 90 }}
                  value={
                    config.sendRateMax
                    ?? config.sendRatePerSecond
                    ?? config.destinationConcurrencyLimit
                    ?? ''
                  }
                  onChange={e => {
                    const n = parseInt(e.target.value);
                    const value = Number.isFinite(n) && n > 0 ? n : undefined;
                    updateConfig('sendRateMax', value);
                    // Clear legacy fields once the user has touched the new pair,
                    // so we don't keep multiple copies of the same intent.
                    updateConfig('sendRatePerSecond', undefined);
                    updateConfig('destinationConcurrencyLimit', undefined);
                  }}
                />
                <span style={{ color: '#6b7280' }}>{(config.sendRateMax ?? 1) === 1 ? 'lead every' : 'leads every'}</span>
                <input
                  type="number"
                  className="form-input"
                  placeholder="1"
                  min={1}
                  style={{ width: 90 }}
                  value={config.sendRateWindowSeconds ?? ''}
                  onChange={e => {
                    const n = parseInt(e.target.value);
                    const value = Number.isFinite(n) && n > 0 ? n : undefined;
                    updateConfig('sendRateWindowSeconds', value);
                  }}
                />
                <span style={{ color: '#6b7280' }}>{(config.sendRateWindowSeconds ?? 1) === 1 ? 'second' : 'seconds'}</span>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Custom Headers (JSON)
                <FieldTooltip text="Optional. Extra HTTP headers as a JSON object. Example: {&quot;Authorization&quot;: &quot;Bearer abc123&quot;, &quot;X-Source&quot;: &quot;databodies&quot;}. Leave blank if not needed." />
              </label>
              <textarea
                className="form-input"
                placeholder='{"Authorization": "Bearer abc123"}'
                rows={4}
                style={{
                  minHeight: 96,
                  fontFamily: 'monospace',
                  fontSize: 13,
                  lineHeight: 1.5,
                  resize: 'vertical',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflow: 'auto',
                }}
                value={headersRaw}
                onChange={e => {
                  const v = e.target.value;
                  setHeadersRaw(v);
                  if (v.trim() === '') {
                    setHeadersError(null);
                    updateConfig('webhookHeaders', undefined);
                    return;
                  }
                  try {
                    const parsed = JSON.parse(v);
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                      setHeadersError(null);
                      updateConfig('webhookHeaders', parsed);
                    } else {
                      setHeadersError('Headers must be a JSON object.');
                    }
                  } catch {
                    // Partial input while typing — keep the text, just flag it.
                    setHeadersError('Not valid JSON yet…');
                  }
                }}
              />
              {headersError && (
                <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{headersError}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Field Mapping
                <FieldTooltip text="Map each destination field to one of your brain's lead fields. If a lead is missing a mapped field's value, that field is just omitted from the payload — the rest of the lead still ships. Leave empty to send the entire lead payload as-is. Toggle a row to R (Required) to force that field to be present: if a Required field is empty on the lead, the destination is NOT attempted and the lead is logged as Failed here, with the missing field name as the reason. JSON only: use dot-notation in a Destination Field Name to nest values — e.g. group.field sends { group: { field: … } }. Use whatever parent/child names your destination's docs require." align="right" />
              </label>
              <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, display: 'flex', alignItems: 'center' }}>
                  <span style={{ flex: 1 }}>Destination Field Name</span>
                  <span style={{ width: 20 }}></span>
                  <span style={{ flex: 1 }}>Lead field</span>
                  <span style={{ width: 60, textAlign: 'center' }}>Req.</span>
                </div>
                {(config.bodyFormat || 'json') === 'json' && (
                  <div style={{ fontSize: 11, color: '#4b5563', background: '#eef2ff', border: '1px solid #dbe1fb', borderRadius: 5, padding: '6px 8px', marginBottom: 8, lineHeight: 1.5 }}>
                    <strong>Nested JSON?</strong> Put a dot in the Destination Field Name to nest it —{' '}
                    <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3, border: '1px solid #e5e7eb' }}>parent.child</code>, using whatever names your destination's docs require. Example:{' '}
                    <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3, border: '1px solid #e5e7eb' }}>group.field</code>{' '}
                    is sent as <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3, border: '1px solid #e5e7eb' }}>{'{ "group": { "field": … } }'}</code>. Multiple <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 3, border: '1px solid #e5e7eb' }}>group.*</code> rows merge into one object.
                  </div>
                )}
                {mappings.length === 0 && (
                  <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0', textAlign: 'center' }}>
                    No mapping &mdash; entire lead payload sent as-is.
                  </div>
                )}
                {mappings.map(({ id, outField, leadField, required }) => (
                  <div key={id} className="form-row" style={{ marginBottom: 6, alignItems: 'center' }}>
                    <input
                      className="form-input"
                      placeholder="first_name"
                      value={outField}
                      onChange={e => updateMapping(id, { outField: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: '#9ca3af', fontSize: 14, padding: '0 4px' }}>&rarr;</span>
                    <select
                      className="form-select"
                      value={leadField}
                      onChange={e => updateMapping(id, { leadField: e.target.value })}
                      style={{ flex: 1 }}
                    >
                      <option value="">&mdash; select a lead field &mdash;</option>
                      {brainFields.map(f => (
                        <option key={f.id} value={f.fieldName}>{f.fieldLabel}</option>
                      ))}
                      <optgroup label="System Fields">
                        {SYSTEM_FIELDS.map(s => (
                          <option key={s.id} value={s.id} title={`e.g. ${s.example}`}>{s.label} — {s.example}</option>
                        ))}
                      </optgroup>
                    </select>
                    {/* O/R toggle — mirrors the Brain field UI. R = the lead is
                        failed (not sent) if this field is empty; O = the field
                        is simply omitted and the lead still ships. */}
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button
                        className="btn-icon"
                        onClick={() => updateMapping(id, { required: !required })}
                        title={required
                          ? 'Required — the lead is failed (not sent) if this field is empty. Click to make optional.'
                          : 'Optional — the field is omitted if empty and the lead still sends. Click to make required.'}
                        style={{
                          width: 28,
                          height: 28,
                          fontSize: 12,
                          fontWeight: 700,
                          color: required ? '#4f46e5' : '#9ca3af',
                          background: required ? '#eef2ff' : undefined,
                          borderColor: required ? '#c7d2fe' : undefined,
                        }}
                      >{required ? 'R' : 'O'}</button>
                      <button
                        className="btn-icon"
                        onClick={() => removeMapping(id)}
                        style={{ color: '#ef4444', width: 28, height: 28 }}
                        title="Remove mapping"
                      >x</button>
                    </div>
                  </div>
                ))}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={addMapping}
                  style={{ marginTop: 6 }}
                >
                  + Add Mapping
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Static Values
                <FieldTooltip text="Hardcoded constants the destination requires that don't come from the lead — e.g. an API list ID, an account secret, a campaign tag. Each row sends `destination_field: literal_value` to the destination, in addition to anything from Field Mapping above. Static values are NOT skipped when leads are missing data — they always send." align="right" />
              </label>
              <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, display: 'flex', alignItems: 'center' }}>
                  <span style={{ flex: 1 }}>Destination Field Name</span>
                  <span style={{ width: 20 }}></span>
                  <span style={{ flex: 1 }}>Hardcoded value</span>
                  <span style={{ width: 32 }}></span>
                </div>
                {statics.length === 0 && (
                  <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0', textAlign: 'center' }}>
                    No static values configured.
                  </div>
                )}
                {statics.map(({ id, outField, value }) => (
                  <div key={id} className="form-row" style={{ marginBottom: 6, alignItems: 'center' }}>
                    <input
                      className="form-input"
                      placeholder="list_id"
                      value={outField}
                      onChange={e => updateStatic(id, { outField: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <span style={{ color: '#9ca3af', fontSize: 14, padding: '0 4px' }}>=</span>
                    <input
                      className="form-input"
                      placeholder="abc-123-xyz"
                      value={value}
                      onChange={e => updateStatic(id, { value: e.target.value })}
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn-icon"
                      onClick={() => removeStatic(id)}
                      style={{ color: '#ef4444', width: 28, height: 28 }}
                      title="Remove value"
                    >x</button>
                  </div>
                ))}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={addStatic}
                  style={{ marginTop: 6 }}
                >
                  + Add Static Value
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Success Condition
                <FieldTooltip text="Optional. By default any 2xx response = delivered. With a success condition: if Field is set (e.g. 'status' or nested path 'data.result'), only that field is checked. If Field is BLANK, the entire response body is searched (great for plain-text replies — use 'contains' with a keyword like 'success' or 'imported'). Matching is CASE-INSENSITIVE: 'success', 'SUCCESS', 'Success' all match the same. Leave Expected Value blank to disable." align="right" />
              </label>
              <div className="form-row">
                <input
                  className="form-input"
                  placeholder="Field (blank = whole body)"
                  value={config.successCondition?.field || ''}
                  onChange={e => updateConfig('successCondition', {
                    ...(config.successCondition || {}),
                    field: e.target.value,
                    operator: config.successCondition?.operator || 'equals',
                    value: config.successCondition?.value || '',
                  })}
                  style={{ flex: 1 }}
                />
                <select
                  className="form-select"
                  value={config.successCondition?.operator || 'equals'}
                  onChange={e => updateConfig('successCondition', { ...(config.successCondition || { field: '', value: '' }), operator: e.target.value })}
                  style={{ width: 130 }}
                >
                  <option value="equals">equals</option>
                  <option value="not_equals">not equals</option>
                  <option value="contains">contains</option>
                  <option value="exists">exists</option>
                  <option value="regex">regex</option>
                </select>
                <input
                  className="form-input"
                  placeholder="Expected value"
                  value={config.successCondition?.value || ''}
                  onChange={e => updateConfig('successCondition', { ...(config.successCondition || { field: '', operator: 'equals' }), value: e.target.value })}
                  style={{ flex: 1 }}
                />
              </div>
            </div>
          </>
        )}

        {/* Time Delay */}
        {legNode.type === 'time_delay' && (
          <div className="form-group">
            <label className="form-label">
              Delay (minutes)
              <FieldTooltip text="How many minutes to wait before continuing to child legs. The lead is queued in Redis/BullMQ and resumes automatically when the timer fires. Use 0 to disable the delay." />
            </label>
            <input
              type="number"
              className="form-input"
              placeholder="5"
              min={0}
              value={config.delayMinutes ?? ''}
              onChange={e => updateConfig('delayMinutes', parseInt(e.target.value) || 0)}
            />
          </div>
        )}

        {/* Condition */}
        {legNode.type === 'condition' && (
          <>
            <div className="form-group">
              <label className="form-label">
                If field
                <FieldTooltip text="The brain field whose value should be evaluated. Pick from the data body's discovered/manual fields — avoids typos. (Advanced enriched-data fields like 'phone_normalized' aren't in this list yet; if you need one, flag it and we'll add a custom-name option.)" />
              </label>
              <select
                className="form-select"
                value={config.condition?.field || ''}
                onChange={e => updateConfig('condition', {
                  ...(config.condition || {}),
                  field: e.target.value,
                  operator: config.condition?.operator || 'equals',
                  value: config.condition?.value || '',
                })}
              >
                <option value="">&mdash; select a field &mdash;</option>
                {brainFields.map(f => (
                  <option key={f.id} value={f.fieldName}>{f.fieldLabel}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Operator</label>
                <select
                  className="form-select"
                  value={config.condition?.operator || 'equals'}
                  onChange={e => updateConfig('condition', { ...config.condition, operator: e.target.value })}
                >
                  <option value="equals">equals</option>
                  <option value="not_equals">not equals</option>
                  <option value="contains">contains</option>
                  <option value="greater_than">greater than</option>
                  <option value="less_than">less than</option>
                  <option value="exists">exists</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">Value</label>
                <input
                  className="form-input"
                  placeholder="80"
                  value={config.condition?.value || ''}
                  onChange={e => updateConfig('condition', { ...config.condition, value: e.target.value })}
                />
              </div>
            </div>
            <div className="form-row" style={{ marginTop: 8 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">
                  If TRUE, route to:
                  <FieldTooltip text="When the condition above evaluates TRUE, the lead is routed to this node next. Pick any other node in the SAME leg as this condition — you can't branch across legs (each leg is its own independent decision tree). Leave blank to do nothing on TRUE." />
                </label>
                <select
                  className="form-select"
                  value={config.trueBranchNodeId || ''}
                  onChange={e => updateConfig('trueBranchNodeId', e.target.value || undefined)}
                >
                  <option value="">&mdash; do nothing &mdash;</option>
                  {siblingLegs.map(l => (
                    <option key={l.id} value={l.id}>{l.name} ({l.type.replace(/_/g, ' ')})</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-label">
                  If FALSE, route to:
                  <FieldTooltip text="When the condition above evaluates FALSE, the lead is routed to this node next. Pick any other node in the SAME leg as this condition — you can't branch across legs. Leave blank to do nothing on FALSE." />
                </label>
                <select
                  className="form-select"
                  value={config.falseBranchNodeId || ''}
                  onChange={e => updateConfig('falseBranchNodeId', e.target.value || undefined)}
                >
                  <option value="">&mdash; do nothing &mdash;</option>
                  {siblingLegs.map(l => (
                    <option key={l.id} value={l.id}>{l.name} ({l.type.replace(/_/g, ' ')})</option>
                  ))}
                </select>
              </div>
            </div>
            {siblingLegs.length === 0 && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6 }}>
                No other nodes in this leg yet &mdash; conditions can only branch to siblings within the same leg. Save this condition, drag more nodes (Webhook Output, Custom API, etc.) into this leg, then come back here to wire up the branches.
              </div>
            )}
          </>
        )}

        {/* Split */}
        {legNode.type === 'split' && (
          <div style={{ padding: 16, background: '#f8f9fb', borderRadius: 8, fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
            <strong style={{ color: '#374151' }}>Split / Fan Out</strong> doesn&apos;t need configuration. Add child legs (using the <code>+</code> button on this leg&apos;s card) and they will all run simultaneously when this node is reached.
          </div>
        )}

            <div className="modal-actions" style={isUrlType ? { justifyContent: 'space-between' } : undefined}>
              {isUrlType && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={openTest}
                  disabled={!config.webhookUrl}
                  title={config.webhookUrl
                    ? 'Send one sample lead to this destination and see the server reply — nothing is saved and your stats are untouched.'
                    : 'Enter a URL above first.'}
                  style={{ borderColor: '#c7d2fe', color: '#4338ca', fontWeight: 600 }}
                >
                  Test A Lead
                </button>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave}>Save Configuration</button>
              </div>
            </div>
          </>
        )}

        {/* Outbound API Log popup — stacked Request bubble over Response bubble.
            Portaled to <body> so it overlays the config modal; its own overlay
            click closes just this popup (stopPropagation keeps it from closing
            the config modal underneath). */}
        {apiLogLead && createPortal(
          <div
            onClick={(e) => { e.stopPropagation(); setApiLogLead(null); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 11000, padding: 20,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(720px, 96vw)', maxHeight: '88vh', overflowY: 'auto',
                background: '#fff', borderRadius: 12, padding: 20,
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>API Log</h2>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                    What we sent to the destination &amp; what came back &middot; {formatReceived(apiLogLead.receivedAt)}
                  </div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setApiLogLead(null)}>Close</button>
              </div>

              {apiLogLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading&hellip;</div>
              ) : !apiLog ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                  No API log recorded for this lead at this leg.
                </div>
              ) : (
                <>
                  {/* Status + latency + retry badges */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '12px 0' }}>
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                      color: apiLog.category === 'delivered' ? '#065f46' : '#991b1b',
                      background: apiLog.category === 'delivered' ? '#d1fae5' : '#fee2e2',
                    }}>
                      {apiLog.statusCode != null ? `HTTP ${apiLog.statusCode}` : 'No response'} &middot; {apiLog.category}
                    </span>
                    {apiLog.latencyMs != null && (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>Responded in {apiLog.latencyMs} ms</span>
                    )}
                    {apiLog.wasRetried && (
                      <span style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '3px 8px', borderRadius: 999 }}>retried</span>
                    )}
                    {apiLog.bodyFormat && (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>&middot; {apiLog.bodyFormat}</span>
                    )}
                  </div>

                  {/* Request bubble */}
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Request</div>
                  <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 2 }}>URL</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: '#111827', marginBottom: 10 }}>
                      {apiLog.requestUrl || <span style={{ color: '#d1d5db' }}>&mdash;</span>}
                    </div>
                    {apiLog.requestHeaders && Object.keys(apiLog.requestHeaders).length > 0 && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 2 }}>Headers</div>
                        <pre style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#111827', margin: '0 0 10px' }}>
                          {JSON.stringify(apiLog.requestHeaders, null, 2)}
                        </pre>
                      </>
                    )}
                    {apiLog.requestBody ? (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 2 }}>Body</div>
                        <pre style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#111827', margin: 0 }}>
                          {apiLog.requestBody}
                        </pre>
                      </>
                    ) : null}
                  </div>

                  {/* Response bubble */}
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Response</div>
                  <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>{apiLog.message}</div>
                    {(() => {
                      const body = apiLog.responseBody || '';
                      const CAP = 300;
                      const long = body.length > CAP;
                      const shown = apiLogFullResponse || !long ? body : body.slice(0, CAP) + '…';
                      return (
                        <>
                          <pre style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#111827', margin: 0 }}>
                            {shown || <span style={{ color: '#d1d5db' }}>(empty response)</span>}
                          </pre>
                          {long && (
                            <button
                              onClick={() => setApiLogFullResponse(v => !v)}
                              style={{ marginTop: 8, cursor: 'pointer', border: 'none', background: 'none', color: '#4f46e5', fontSize: 12, fontWeight: 600, padding: 0 }}
                            >
                              {apiLogFullResponse ? 'Show less' : `Show full (${body.length} chars)`}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body
        )}

        {/* Test A Lead popup — pre-populated sample lead + live server reply.
            Portaled to <body> so it overlays the config modal. */}
        {testOpen && createPortal(
          <div
            onClick={(e) => { e.stopPropagation(); setTestOpen(false); }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 11000, padding: 20,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(640px, 96vw)', maxHeight: '88vh', overflowY: 'auto',
                background: '#fff', borderRadius: 12, padding: 20,
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Test A Lead</h2>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                    Sends one sample lead to <span style={{ fontFamily: 'monospace' }}>{config.webhookMethod || 'POST'}</span> {config.webhookUrl || '(no URL)'} &middot; nothing is saved, your stats are untouched.
                  </div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => setTestOpen(false)}>Close</button>
              </div>

              {/* Editable sample fields */}
              <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, margin: '14px 0' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  Sample lead
                </div>
                {testFields.length === 0 ? (
                  <div style={{ fontSize: 13, color: '#9ca3af' }}>No fields to send.</div>
                ) : (
                  testFields.map(row => (
                    <div key={row.leadField} className="form-row" style={{ marginBottom: 8, alignItems: 'center' }}>
                      <label
                        style={{ flex: 1, fontSize: 13, color: '#374151' }}
                        title={`Sent as: ${row.destLabels.join(', ')}`}
                      >
                        {row.destLabels.join(', ')}
                        <span style={{ color: '#9ca3af' }}> &larr; {row.leadField}</span>
                      </label>
                      <input
                        className="form-input"
                        value={row.value}
                        disabled={row.system}
                        onChange={e => updateTestField(row.leadField, e.target.value)}
                        style={{ flex: 1, ...(row.system ? { color: '#9ca3af', fontStyle: 'italic', background: '#f3f4f6' } : {}) }}
                      />
                    </div>
                  ))
                )}
                {statics.filter(s => s.outField.trim()).length > 0 && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                    Plus static values (always sent): {statics.filter(s => s.outField.trim()).map(s => `${s.outField.trim()}=${s.value}`).join(', ')}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                <button
                  className="btn btn-primary"
                  onClick={runTest}
                  disabled={testRunning || !config.webhookUrl}
                >
                  {testRunning ? 'Sending…' : 'Submit Test'}
                </button>
              </div>

              {/* Result */}
              {testError && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontSize: 13 }}>
                  {testError}
                </div>
              )}
              {testResult && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                    <span style={{
                      fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
                      color: testResult.success ? '#065f46' : '#991b1b',
                      background: testResult.success ? '#d1fae5' : '#fee2e2',
                    }}>
                      {testResult.success ? '✓ Accepted' : '✕ Not accepted'}
                      {testResult.statusCode != null ? ` · HTTP ${testResult.statusCode}` : ''}
                      {testResult.category ? ` · ${testResult.category}` : ''}
                    </span>
                    {testResult.bodyFormat && (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>&middot; {testResult.bodyFormat}</span>
                    )}
                  </div>

                  {/* What we sent */}
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Request</div>
                  <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: '#111827', marginBottom: testResult.requestBody ? 10 : 0 }}>
                      {testResult.requestUrl || <span style={{ color: '#d1d5db' }}>&mdash;</span>}
                    </div>
                    {testResult.requestBody ? (
                      <pre style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#111827', margin: 0 }}>
                        {testResult.requestBody}
                      </pre>
                    ) : null}
                  </div>

                  {/* Server reply */}
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Server reply</div>
                  <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>{testResult.message}</div>
                    {(() => {
                      const body = testResult.responseBody || '';
                      const CAP = 300;
                      const long = body.length > CAP;
                      const shown = testFullResponse || !long ? body : body.slice(0, CAP) + '…';
                      return (
                        <>
                          <pre style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#111827', margin: 0 }}>
                            {shown || <span style={{ color: '#d1d5db' }}>(empty response)</span>}
                          </pre>
                          {long && (
                            <button
                              onClick={() => setTestFullResponse(v => !v)}
                              style={{ marginTop: 8, cursor: 'pointer', border: 'none', background: 'none', color: '#4f46e5', fontSize: 12, fontWeight: 600, padding: 0 }}
                            >
                              {testFullResponse ? 'Show less' : `Show full (${body.length} chars)`}
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}

export default LegNodeConfigModal;
